<template>
	<AppModal id="mfa-validation-modal" @close="close" @open="handleModalOpen">
		<div v-if="!showRecoveryTextInput">
			<div class="mfa-modal-header">
				<h3 class="text-bold mb-2">Verify Code</h3>
				<p class="modal-pretext text-light">
					Enter the one-time code from <strong>{{ this.configName === '' ? 'authenticator app' : this.configName }}</strong> to complete
					authentication
				</p>
			</div>
			<OTPInput
				ref="otpInput"
				:error="error"
				:warning="warning"
				:state-change-key="stateChangeKey"
				@resetErrors="resetWarningsAndErrors"
				@close="resetWarningsAndErrors"
				submit-button-text="Continue"
				@submit="validateConfigOTP"
				@showRecovery="showRecoveryTextInput = true"
				:showHelpAndRecovery="true"
			/>
		</div>
		<div v-else>
			<div class="mfa-modal-header">
				<h3 class="text-bold mb-2">Enter your recovery code</h3>
				<p class="modal-pretext text-light">
					Use the one-time-use recovery code you saved during 2FA setup to sign-in
				</p>
			</div>
			<RecoveryTextInput
				:error="error"
				:warning="warning"
				:state-change-key="stateChangeKey"
				@resetErrors="resetWarningsAndErrors"
				submit-button-text="Continue"
				@submit="submitRecoveryText"
				@showOTP="showRecoveryTextInput = false"
				:loading="isLoading"
			></RecoveryTextInput>
		</div>
	</AppModal>
</template>
<script>
	import { mapActions, mapGetters } from 'vuex'
	/* eslint-disable import/extensions */
	import { configSettings } from '~/plugins/config-settings.js'
	import axios from 'axios'
	import helper from '~/global_helper/helpers.js'
	import CONSTANTS from '../../global_helper/constants'
	import RecoveryTextInput from '../RecoveryTextInput'

	export default {
		props: {
			nextRoute: String,
			token: String,
			configName: {
				type: String,
				default: '',
			},
		},
		components: {
			RecoveryTextInput,
		},
		data() {
			return {
				ajaxWarnings: {},
				ajaxError: {
					message: '',
					status: null,
					errors: {},
				},
				form: {},
				loading: false,
				stateChangeKey: 0,
				size: 300,
				ttlDays: CONSTANTS.MFA.DEFAULT_TTL_DAYS,
				formStatus: false,
				showRecoveryTextInput: false,
			}
		},
		computed: {
			...mapGetters('Auth', ['fulfillMFAPromise', 'fulfillMFAPromiseResolve']),
			error() {
				return (this.ajaxError && this.ajaxError.errors && this.ajaxError.errors.otp) || []
			},
			warning() {
				return this.ajaxWarnings && this.ajaxWarnings.otp
			},
			isLoading() {
				return this.loading
			},
		},
		methods: {
			...mapActions('Auth', ['saveToken']),
			handleModalOpen() {
				// Focus first input when modal opens
				if (this.$refs.otpInput && !this.showRecoveryTextInput) {
					this.$refs.otpInput.focusFirstInput()
				}
			},
			async updateTokenAfterCodeVerification(newToken) {
				await this.$store.dispatch('Auth/saveToken', { token: newToken })
				this.continueAsIntended()
			},

			continueAsIntended() {
				$('#mfa-validation-modal').modal('hide')

				// By resolving the MFA promise, callbacks
				// dependent on a successful flow can be executed
				this.fulfillMFAPromiseResolve()
			},
			close() {
				this.$emit('close')
				this.showRecoveryTextInput = false
				this.resetWarningsAndErrors()
				this.stateChangeKey += 1
			},
			resetWarningsAndErrors() {
				this.ajaxError.errors = []
				this.ajaxWarnings = {}
			},
			submitRecoveryText(text) {
				this.resetWarningsAndErrors()
				this.stateChangeKey += 1
				this.formStatus = true
				const errorObject = {
					status: 'error',
					message: this.$t('The given data was invalid.'),
					errors: {},
				}
				this.stateChangeKey += 1

				if (text?.trim().length === 0 || text?.trim().length > 128) {
					Object.assign(errorObject.errors, {
						otp: [this.$t('Invalid recovery text format')],
					})
					this.formStatus = false
				}

				Object.assign(this.ajaxError, errorObject)

				if (this.formStatus) {
					const recoveryPayload = {
						recovery_text: text?.trim(),
					}
					axios
						.post('v1/mfa/recovery', recoveryPayload, {
							baseURL: configSettings.authApiUrl,
							headers: {
								Authorization: `Bearer ${this.token}`,
							},
						})
						.then(response => {
							// After successful reset trigger the modal to nudge reconfiguration
							// A little delay so that whereever they are it will render properly
							this.fulfillMFAPromise.then(() => {
								setTimeout(() => {
									$('#mfa-configuration-modal').modal('show')
								}, 2000)
							})
							this.updateTokenAfterCodeVerification(response.data.token)
						})
						.catch(error => {
							this.$logError(error.response.data)
							if (error.response.status === 401) {
								Object.assign(errorObject.errors, {
									otp: [this.$t(`Session expired. Please log in again`)],
								})
							} else if (error.response.status === 400) {
								Object.assign(errorObject.errors, {
									otp: [this.$t(error.response.data.status)],
								})
							}

							this.ajaxError = { ...errorObject }
							this.stateChangeKey += 1
						})
						.finally(() => {
							this.loading = false
						})
				}
			},
			validateConfigOTP(code) {
				this.resetWarningsAndErrors()
				this.stateChangeKey += 1
				this.formStatus = true
				const errorObject = {
					status: 'error',
					message: this.$t('The given data was invalid.'),
					errors: {},
				}
				this.stateChangeKey += 1

				if (!helper.validateMFACodeFormat(code?.trim())) {
					Object.assign(errorObject.errors, {
						otp: [this.$t('Invalid code format. Please enter a valid 6-digit code.')],
					})
					this.formStatus = false
				}

				Object.assign(this.ajaxError, errorObject)

				if (this.formStatus) {
					const otpPayload = {
						totp: code?.trim(),
						ttl_days: CONSTANTS.MFA.DEFAULT_TTL_DAYS, // maximum ttl days
						ttl_start: Math.floor(Date.now() / 1000),
					}
					axios
						.post(helper.removePrefix(this.nextRoute, '/api/'), otpPayload, {
							baseURL: configSettings.authApiUrl,
							headers: {
								Authorization: `Bearer ${this.token}`,
							},
						})
						.then(response => {
							this.updateTokenAfterCodeVerification(response.data.token)
						})
						.catch(error => {
							this.$logError(error.response.data)
							if (error.response.status === 401) {
								Object.assign(errorObject.errors, {
									otp: [this.$t(`Session expired. Please log in again`)],
								})
							} else if (error.response.status === 400) {
								Object.assign(errorObject.errors, {
									otp: ['Please enter a valid 6-digit code.'],
								})
							}

							this.ajaxError = { ...errorObject }
							this.stateChangeKey += 1
						})
						.finally(() => {
							this.loading = false
						})
				}
			},
		},
	}
</script>

<style lang="scss" scoped>
	.mfa-modal-header {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		line-height: normal;
		color: var(--stan-text-dark-color);

		.modal-pretext {
			margin: 5px;
		}
	}
</style>
